import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TagBadge } from '@/components/tags/tag-badge';
import { FunnelStageBadge } from '@/components/funnel/funnel-stage-badge';
import { TagPicker } from '@/components/tags/tag-picker';
import { useUserTags, useAssignTag, useUnassignTag } from '@/hooks/use-tags';
import type { Tag } from '@/types/tags';

interface StudentTagsCardProps {
  studentId: string;
}

// Surfaces the student's current funnel stage, milestones, labels, and
// segments on the student-detail page. Teachers can add/remove labels
// inline via the TagPicker.
export function StudentTagsCard({ studentId }: StudentTagsCardProps) {
  const { data: tags = [], isLoading } = useUserTags(studentId);
  const assign = useAssignTag();
  const unassign = useUnassignTag();

  const stage = tags.find((t) => t.kind === 'stage');
  const milestones = tags.filter((t) => t.kind === 'milestone');
  const segments = tags.filter((t) => t.kind === 'segment');
  const labels = tags.filter((t) => t.kind === 'label');
  const labelIds = labels.map((l) => l.id);

  const toggle = async (tagId: string) => {
    const existing = labels.find((l) => l.id === tagId);
    try {
      if (existing) {
        await unassign.mutateAsync({ id: tagId, input: { user_ids: [studentId] } });
        toast.success('Tag removed');
      } else {
        await assign.mutateAsync({ id: tagId, input: { user_ids: [studentId] } });
        toast.success('Tag added');
      }
    } catch {
      toast.error('Tag update failed');
    }
  };

  const removeLabel = async (tag: Tag) => {
    try {
      await unassign.mutateAsync({ id: tag.id, input: { user_ids: [studentId] } });
      toast.success(`Removed ${tag.name}`);
    } catch {
      toast.error('Failed to remove tag');
    }
  };

  if (isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Tags</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-muted-foreground">Stage</span>
          {stage ? (
            <FunnelStageBadge stage={stage} />
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        {milestones.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-xs text-muted-foreground">Milestones</span>
            {milestones.map((m) => (
              <TagBadge key={m.id} tag={m} />
            ))}
          </div>
        )}

        {segments.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-xs text-muted-foreground">Segments</span>
            {segments.map((s) => (
              <TagBadge key={s.id} tag={s} />
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-muted-foreground">Labels</span>
          {labels.map((l) => (
            <TagBadge key={l.id} tag={l} onRemove={() => removeLabel(l)} />
          ))}
          <TagPicker
            selectedIds={labelIds}
            onToggle={toggle}
            kind="label"
            triggerLabel="Add label"
          />
        </div>
      </CardContent>
    </Card>
  );
}
